import { forwardRef } from 'react';

import { Accordion as BaseAccordion } from '@base-ui/react/accordion';
import { cn } from 'components/utils';

import {
  accordionActionsRecipe,
  accordionDescriptionRecipe,
  accordionHeaderRecipe,
  accordionIndicatorRecipe,
  accordionItemRecipe,
  accordionLeadingRecipe,
  accordionMetaRowRecipe,
  accordionPanelContentRecipe,
  accordionPanelRecipe,
  accordionRootRecipe,
  accordionTitleRecipe,
  accordionTitlesRecipe,
  accordionTrailingRecipe,
  accordionTriggerRecipe,
} from './Accordion.recipe';
import { AccordionLayoutContext, useAccordionLayout } from './context';

import type { AccordionItemProps, AccordionProps } from './types';

/**
 * The four indicator glyphs, transcribed at 1.5px in a 16px box.
 *
 * `CaretDown` is the same vector `Select`'s caret uses — node I498:41227, a 10x5 stroke from (3,6)
 * to (13,6). `CaretRight` is its 90-degree sibling drawn rather than rotated, so the two are
 * interchangeable without a transform. `Plus` and `X` are the Phosphor shapes the set's stacked
 * cells instantiate, scaled from Phosphor's 256 box to 16.
 *
 * Drawn here rather than imported from `icons` for the reason the previous revision gave and which
 * still holds: `src/icons` is MUI-backed (`createSvgIcon`, `createMaterialIcon`), so importing from
 * it would pull MUI sizing and theming into a Design System 2.0 primitive that has neither. HNS-262
 * will likely replace these and `Select`'s copy with a library import; until it lands, neither
 * component owns the other's icon.
 *
 * Stroke weight is 1.5px to match `Select`. The library's icons are authored at 1.2px, so these are
 * one notch heavier than a `Library` glyph beside them — consistency inside the component was
 * chosen over consistency with the icon set, on the grounds that the caret's twin is the select's
 * and not the library's. Recorded as a gap.
 *
 * `aria-hidden` and `focusable="false"` on every one: the open state is announced by `aria-expanded`
 * on the trigger, so a second announcement here would be noise, and IE-era SVGs are tab stops
 * without the second attribute.
 */
const glyphProps = {
  'aria-hidden': true,
  fill: 'none',
  focusable: 'false',
  height: '16',
  viewBox: '0 0 16 16',
  width: '16',
} as const;

const strokeProps = {
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  strokeWidth: '1.5',
} as const;

/**
 * Which of the two paths is showing is decided by CSS, reading the trigger's `data-panel-open`
 * through `group/accordion-trigger`.
 *
 * A selector rather than a branch on Base UI's item state, and the reason is worth recording
 * because the obvious alternative looks cleaner: `Accordion.Trigger` does accept a render-function
 * child, so `{({ open }) => …}` would work. It would also make the whole trigger's children a
 * function, which puts the eyebrow row, the title block and both slots inside a closure that
 * re-runs on every state change — to swap two `<path>` elements.
 *
 * The cost of doing it in CSS is one extra `<path>` per row, mounted and hidden. `display: none`
 * takes it out of the accessibility tree and out of layout, and the glyph is `aria-hidden` either
 * way, so nothing is announced twice and nothing is measured twice.
 */
const CLOSED_ONLY = 'group-data-[panel-open]/accordion-trigger:hidden';
const OPEN_ONLY = 'hidden group-data-[panel-open]/accordion-trigger:inline';

/** `CaretRight` closed, `CaretDown` open — the disclosure convention, drawn rather than rotated. */
const InlineIndicatorIcon = () => (
  <svg {...glyphProps}>
    <path className={CLOSED_ONLY} d="M6 3L11 8L6 13" {...strokeProps} />
    <path className={OPEN_ONLY} d="M3 6L8 11L13 6" {...strokeProps} />
  </svg>
);

/** `Plus` closed, `X` open — the set's stacked pair, where the row is a card rather than a line. */
const StackedIndicatorIcon = () => (
  <svg {...glyphProps}>
    <path className={CLOSED_ONLY} d="M3.5 8H12.5" {...strokeProps} />
    <path className={CLOSED_ONLY} d="M8 3.5V12.5" {...strokeProps} />
    <path className={OPEN_ONLY} d="M12.5 3.5L3.5 12.5" {...strokeProps} />
    <path className={OPEN_ONLY} d="M3.5 3.5L12.5 12.5" {...strokeProps} />
  </svg>
);

/**
 * Design System 2.0 accordion — a list of titled sections that expand and collapse.
 *
 * Ported from Figma 1447:135303 on 2026-09-18. Its surface and layout axes are public props; state,
 * open and disabled are supplied by the browser and Base UI. See `types.ts` for the public contract
 * and `Accordion.recipe.ts` for where each visual state is drawn.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer,
 * so the same markup is correct in both themes, and `.ds-accordion` needs no dark rule at all to
 * achieve it.
 *
 * ── What this component is NOT ────────────────────────────────────────────────────────────────
 *
 * It is not a tab set, and the difference is keyboard behaviour rather than appearance. Arrow keys
 * do nothing here: every row is an ordinary tab stop, and moving between them is Tab. That is the
 * accordion pattern, and it is also what Base UI 1.7.0 now implements — the APG guidance that
 * once put roving focus on an accordion was withdrawn, and the library's `orientation` and
 * `loopFocus` props are deprecated no-ops as a result. `types.ts` drops both rather than exposing
 * a knob that turns nothing.
 *
 * It is also not a way to hide content from the page while keeping it in the DOM. A collapsed
 * panel is UNMOUNTED, which is what `ExpandableBox` does and what callers of it expect: content
 * behind a closed section does not appear in a find-in-page, does not run its effects, and does
 * not make its network requests. Base UI can do the opposite through `keepMounted` and
 * `hiddenUntilFound`; both are dropped from this component's props, because a caller flipping one
 * would silently change all three of those properties for everything inside.
 *
 * It is not a row with two actions. The trigger is one `<button>` covering the whole row, which is
 * what makes the row clickable, and that forecloses a second control inside it — see `trailing` in
 * `types.ts`.
 *
 * ── Single or multiple open ───────────────────────────────────────────────────────────────────
 *
 * `multiple` is the caller's choice and is forwarded untouched, because a headless accordion
 * supports both at no cost. The default is Base UI's: one open section at a time. Note what single
 * mode does NOT do — there is no "one must stay open" behaviour, so pressing the open row's
 * trigger collapses it and leaves the list with nothing open. That is the pattern's normal
 * behaviour rather than an omission; a caller needing a section always open owns `value`.
 */
export const Accordion = ({
  children,
  className,
  iconPlacement = 'end',
  layout = 'inline',
  surface = 'on-surface',
  ...rest
}: AccordionProps) => (
  /**
   * `data-icon-placement`, `data-layout` and `data-surface` are read by selectors in the recipes
   * and in `design-system.css` rather than by any code here, which is what keeps three purely
   * visual choices out of React. They are set unconditionally so a test can read any of them for
   * either value, not only for the non-default one.
   *
   * `layout` and `surface` ALSO travel through context, because two of the parts need them to pick
   * a cva variant rather than to match a selector — the title's type level and the trigger's
   * insets are different class lists, not different values of one custom property, and a
   * `group-data` selector cannot swap a class list. The attribute and the context always agree;
   * the attribute is the one a test or a parent stylesheet should read.
   */
  <AccordionLayoutContext.Provider value={{ layout, surface }}>
    <BaseAccordion.Root
      className={cn(accordionRootRecipe({ surface }), className)}
      data-icon-placement={iconPlacement}
      data-layout={layout}
      data-surface={surface}
      {...rest}
    >
      {children}
    </BaseAccordion.Root>
  </AccordionLayoutContext.Provider>
);

/**
 * One titled section.
 *
 * A separate exported component rather than a `sections` array prop, so the content between the
 * tags is ordinary JSX — the whole requirement is "arbitrary content", and an array of render
 * functions is a worse way to write arbitrary content than children are.
 *
 * The parts are assembled here rather than left to the caller because their relationship is
 * what makes the thing accessible: the trigger is a real `<button>` inside Base UI's `<h3>`, so
 * the section appears in the document's heading outline and a screen reader can jump to it; the
 * button carries `aria-expanded`, and `aria-controls` pointing at the panel WHILE OPEN — Base UI
 * omits it when closed, since the panel it would name is not in the DOM to be pointed at; and the
 * panel is a `role="region"` labelled by the trigger. None of that is something a caller should
 * have to reassemble correctly.
 *
 * `data-part` names each element so a parent can target it and a test can assert on it without
 * depending on a styling class, the same convention the field pair uses for its box.
 */
export const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  (
    {
      children,
      className,
      description,
      leading,
      panelClassName,
      showIndicator = true,
      title,
      trailing,
      triggerClassName,
      ...rest
    },
    ref
  ) => {
    const { layout, surface } = useAccordionLayout();

    const indicator = showIndicator ? (
      <span className={accordionIndicatorRecipe({ layout })} data-part="indicator">
        {layout === 'stacked' ? <StackedIndicatorIcon /> : <InlineIndicatorIcon />}
      </span>
    ) : null;

    const titles = (
      <span className={accordionTitlesRecipe()}>
        <span className={accordionTitleRecipe({ layout })} data-part="title">
          {title}
        </span>

        {description ? (
          <span className={accordionDescriptionRecipe()} data-part="description">
            {description}
          </span>
        ) : null}
      </span>
    );

    return (
      <BaseAccordion.Item
        ref={ref}
        className={cn(accordionItemRecipe({ layout, surface }), className)}
        data-part="item"
        {...rest}
      >
        <BaseAccordion.Header className={accordionHeaderRecipe()} data-part="header">
          <BaseAccordion.Trigger
            className={cn(accordionTriggerRecipe({ layout, surface }), triggerClassName)}
            data-part="trigger"
          >
            {layout === 'stacked' ? (
              <>
                {/* The eyebrow. The set expects `leading` to be filled here — see `types.ts`. */}
                <span className={accordionMetaRowRecipe()} data-part="meta-row">
                  {leading ? (
                    <span className={accordionLeadingRecipe()} data-part="leading">
                      {leading}
                    </span>
                  ) : null}

                  <span className={accordionActionsRecipe()} data-part="actions">
                    {trailing ? (
                      <span className={accordionTrailingRecipe()} data-part="trailing">
                        {trailing}
                      </span>
                    ) : null}

                    {indicator}
                  </span>
                </span>

                {titles}
              </>
            ) : (
              <>
                {indicator}

                {leading ? (
                  <span className={accordionLeadingRecipe()} data-part="leading">
                    {leading}
                  </span>
                ) : null}

                {titles}

                {trailing ? (
                  <span className={accordionTrailingRecipe()} data-part="trailing">
                    {trailing}
                  </span>
                ) : null}
              </>
            )}
          </BaseAccordion.Trigger>
        </BaseAccordion.Header>

        {/* The padding lives on the inner wrapper, not on the panel — see `accordionPanelRecipe`. */}
        <BaseAccordion.Panel className={accordionPanelRecipe()} data-part="panel">
          <div
            className={cn(accordionPanelContentRecipe({ layout, surface }), panelClassName)}
            data-part="panel-content"
          >
            {children}
          </div>
        </BaseAccordion.Panel>
      </BaseAccordion.Item>
    );
  }
);
